import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _ from 'lodash';
import {TooltipIcon, InlineTooltip} from '../../Tooltip';
import InlineFieldLayout from './InlineFieldLayout';
import {getDisplayName} from '../../../proptypes/ComponentPropTypes';
import {isEmptyValue} from '../fieldValues';
import {log} from '@elixir/logger';
import styles from './FieldComponent-hashed.scss';
import gridStyles from '@elixir/layout/grid/Grid-hashed.scss';
import defaultTranslator from '@elixir/translator';
import FieldLabel from './FieldLabel';
import FieldMessage from './FieldMessage';
import getIconClasses from '../../../iconUtils';
import {renderWithBreakpoint} from '@elixir/layout';
import {defaultTheme} from '@elixir/theme';

export const VISIBLE_IF_REQUIRED = '{REQUIRED}';

/**
 * Base class for field components. A field component renders label, control and messages. Other fields should extend
 * this class. Override any methods as needed: `renderLabel()`, `renderControl()`, `renderMessages()`. By default,
 * this class will render an <input> control.
 */

export default class FieldComponent extends PureComponent {

    static propTypes = {
        /**
         * Used to identify the component. applied to control and referenced by label
         */
        id: PropTypes.string.isRequired,
        /**
         * Label text to display; if not provided, uses '[id]' for development
         */
        label: PropTypes.string,
        /**
         * Tooltip text to display or tooltip object to pass to TooltipIcon
         */
        tooltip: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object
        ]),
        /**
         * Placeholder to display on empty component
         */
        placeholder: PropTypes.string,
        /**
         * If true, this field is required
         */
        required: PropTypes.bool,
        /**
         * If true, this field is readonly
         */
        readOnly: PropTypes.bool,
        /**
         * If true, this field is disabled
         */
        disabled: PropTypes.bool,
        /**
         * Value to display in control
         */
        value: PropTypes.string,
        /**
         * Set the default field value on render if there is a default value; needs onValueChange to work
         */
        defaultValue: PropTypes.string,
        /**
         * If true, will automatically trim string values on change
         */
        autoTrim: PropTypes.bool,
        /**
         * Callback when value is changed; receives new value and (model or path) for this component
         */
        onValueChange: PropTypes.func,
        /**
         * Callback when blur event is fired
         */
        onBlur: PropTypes.func,
        /**
         * Callback when focus event is fired
         */
        onFocus: PropTypes.func,
        /**
         * Passed as second argument to onValueChange
         */
        model: PropTypes.object,
        /**
         * Passed as second argument to onValueChange if model is not present
         */
        path: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.arrayOf(PropTypes.string)
        ]),
        /**
         * Show errors for this field
         */
        showErrors: PropTypes.bool,
        /**
         * Validation messages to show for this field; only rendered if 'showErrors' is true
         */
        validationMessages: PropTypes.array,
        /**
         * Layout to use with this field; default is more control and less label; other option is 'reversed'
         */
        layout: PropTypes.string,
        /**
         * Hides the label on any layout
         */
        hideLabel: PropTypes.bool,
        /**
         * Additional style to apply to the component
         */
        className: PropTypes.string,
        /**
         * Additional style to apply to the control of the component
         */
        controlClassName: PropTypes.string,
        /**
         * Additional style to apply to the label of the component
         */
        labelClassName: PropTypes.string,
        /**
         * The automation property to be applied to the field
         */
        gwTest: PropTypes.string,
        /**
         * Used to display or not the validation icon
         */
        showValidationIcon: PropTypes.bool,
        /**
         * The full path of view model
         */
        dataPath: PropTypes.string,
        /**
         * An object which should contain a regex pattern as string and a validation message as string
         */
        validator: PropTypes.shape({
            pattern: PropTypes.string.isRequired,
            message: PropTypes.string.isRequired
        }),
        /**
         * Used to override the default required field message
         */
        requiredFieldValidationMessage: PropTypes.string,
        /**
         * Success message to apply to component if it is valid
         */
        successMessage: PropTypes.string,
        /**
         * Theme to apply to component
         */
        theme: PropTypes.object
    };

    static defaultProps = {
        autoTrim: false,
        required: false,
        readOnly: false,
        disabled: false,
        showErrors: false,
        hideLabel: false,
        showValidationIcon: false,
        dataPath: '',
        theme: defaultTheme
    };

    /**
     * Define context properties that are set somewhere higher in the component chain.
     * Useful for passing down functions and settings without having to pass down as a prop.
     * Used here to obtain the translator function for localizing strings
     *
     * @type {Object}
     * @property {function(key: string)} translator - The function used to translate strings
     */
    static contextTypes = {
        translator: PropTypes.func.isRequired
    };

    /**
     * The default string to present for the read-only rendering of a component with no value
     * @type {string}
     */
    static defaultROEmptyValue = '-';

    /**
     * The special `defaultValue` prop value that works with `availableValues` arrays to set the default to the first
     * value in the array.
     * @type {string}
     */
    static firstAvailable = '__FIRST_AVAILABLE__';

    /**
     * The special `defaultValue` prop value that works with `availableValues` arrays to set the default to the last
     * value in the array.
     * @type {string}
     */
    static lastAvailable = '__LAST_AVAILABLE__';

    /**
     * The special `defaultValue` prop value that works with `availableValues` arrays to set the default to the only
     * value in the array.
     * @type {string}
     */
    static onlyAvailable = '__ONLY_AVAILABLE__';

    /**
     * Return translator from context or default translator if
     * the former is undefined
     *
     * @returns {function}
     */
    get translator () {
        return this.context.translator || defaultTranslator;
    }

    /**
     * Part of the React lifecycle:  It will set the pristine setting.
     */
    componentWillMount () {
        this.pristine = true;
        this.focused = false;
        this.focusPristine = true;
        this.beforeValue = null;
    }

    /**
     * Part of the React lifecycle:  It will deal with assigning the `value` to be the
     * `defaultValue` when the `value` is `undefined`.
     */
    componentDidMount () {
        const {defaultValue, value, availableValues} = this.props;
        if (defaultValue !== undefined && (value === undefined || value === null)) {
            if (defaultValue === FieldComponent.firstAvailable) { // default to first available
                if (availableValues) {
                    this.notifyChange(availableValues[ 0 ].code);
                }
            }
            else if (defaultValue === FieldComponent.lastAvailable) { // default to last available
                if (availableValues) {
                    this.notifyChange(availableValues[ availableValues.length - 1 ].code);
                }
            }
            else if (defaultValue === FieldComponent.onlyAvailable) { // default to only available
                if (availableValues && availableValues.length === 1) {
                    this.notifyChange(availableValues[ 0 ].code);
                }
            }
            else {
                this.notifyChange(defaultValue);
            }
        }
    }

    /**
     * Determines whether errors should be displayed. Errors are displayed as highlight of control and as separate
     * message block.
     *
     * @returns {boolean} true if errors should be shown; false if errors should be hidden
     */
    showErrors () {
        // do not show errors if focused and changes have been made since gaining focus
        if (this.focused && !this.focusPristine) {
            return false;
        }
        // show errors if forced or value has changed since mount
        const {showErrors} = this.props;
        return (showErrors === true || !this.pristine);
    }

    /**
     * Default change handler for `input` element. It will be used unless this method or `renderControl()`
     * is overridden.
     *
     * @param {Object} evt - React event wrapper
     */
    handleChange = evt => {
        let value = evt.target.value;
        // TBD: handle this with input mask instead
        if (typeof value === 'string' && this.props.autoTrim) {
            value = value.trim();
        }
        this.notifyChange(value);
    };

    /**
     * Handle 'focus' events for 'input' elements with validation.
     * This method is not included by default. Derived class will need to include this in their `renderControl()` method
     *
     * @param {Object} evt - React event wrapper
     */
    handleFocus = evt => {
        const {onFocus} = this.props;

        this.focused = true;
        this.focusPristine = true;

        // invoke any 'onFocus' callback passed in as props
        if (onFocus) {
            onFocus(evt, this.beforeValue);
        }

        // this.props.value is a reference when using a non primitive such as a function.
        // this can lead to issues where if you call forceUpdate the beforeValue gets updated with the new value
        this.beforeValue = _.cloneDeep(this.props.value);
    };

    /**
     * Get focus/blur handlers for use in derived input fields. This optimizes the validation behavior when the
     * input field is in focus.
     *
     * @returns {Object} focus and blur handlers to be added to `<input>` fields
     */
    getInputFocusHandlers () {
        return {
            onFocus: this.handleFocus,
            onBlur: this.handleBlur
        };
    }

    /**
     * Get the validation messages to display.
     * If field is 'required' and no validation message is provided, it is added
     *
     * @returns {Array} validation messages
     */
    getValidationMessages () {
        const {
            validationMessages,
            required,
            requiredFieldValidationMessage,
            validator
        } = this.props;
        const value = this.getValue();

        if (required && this.isEmpty() && _.isEmpty(validationMessages)) {
            const {translator} = this;
            return [ requiredFieldValidationMessage || translator({
                id: 'policycommon.This is a required field',
                defaultMessage: 'This is a required field'
            }) ];
        }
        if (validator && !this.isEmpty()) {
            const {pattern, message} = validator;
            if (pattern && message && !value.match(pattern)) {
                const {translator} = this;
                return [ translator(message) ];
            }
        }
        return validationMessages;
    }

    getValue () {
        return this.props.value;
    }

    /**
     * Tests if component is empty
     *
     * @returns {boolean}
     */
    isEmpty () {
        return isEmptyValue(this.props.value);
    }

    /**
     * Handle 'focus' events for 'input' elements with validation.
     * This method is not included by default. Derived class will need to include this in their `renderControl()` method
     *
     * @param {Object} evt - React event wrapper
     */
    handleBlur = evt => {
        const {
            onBlur
        } = this.props;

        const validationMessages = this.getValidationMessages();
        const {translator} = this;

        this.focused = false;
        // if change was made while focused and there is an error, force update to refresh on blur
        if (!this.focusPristine && (!_.isEmpty(validationMessages) || !this.pristine)) {
            this.forceUpdate();
        }
        // invoke any 'onBlur' callback passed in as props
        if (onBlur) {
            const {
                id, label, value, model, path, dataPath
            } = this.props;
            onBlur(evt, {
                id,
                label: translator(label),
                value,
                beforeValue: this.beforeValue,
                model: model || path,
                dataPath
            });
        }
    };

    /**
     * Helper method to invoke callback when value is changed; useful in derived classes
     *
     * @param {any} value - new value of control to send with notification
     * @param {Object} model || path - The viewmodel or path
     * @param {String} id - The id of the field
     * @param {String} label - The label of the field
     * @param {Object} beforeValue - The before value of the field
     */
    notifyChange (value) {
        const {translator} = this;
        this.pristine = false;
        this.focusPristine = false;
        const {
            onValueChange, model, path, id, label, dataPath
        } = this.props;
        if (onValueChange) {
            onValueChange(
                value,
                model || path,
                {
                    id,
                    label: translator(label),
                    beforeValue: this.beforeValue,
                    dataPath
                }
            );
        }
    }

    renderInlineTooltip () {
        const {id, tooltip} = this.props;
        if (!tooltip) {
            return null;
        }

        const {
            text = tooltip, icon, title, href, linkIcon
        } = tooltip;
        // avoid destructuring string tooltip setting link to string.prototype.link()
        const link = _.isObject(tooltip) ? tooltip.link : undefined;

        if (!text && !title && (!link || !href)) {
            return null;
        }

        const tooltipProps = {
            id: `${id}-inlinetooltip`,
            text,
            title,
            icon,
            link,
            href,
            linkIcon
        };

        return <InlineTooltip {...tooltipProps} />;
    }

    renderTooltipIcon = (isOnPhone) => {
        const {tooltip, layout} = this.props;
        if (!tooltip || layout === 'plain') {
            return null;
        }

        const {
            text = tooltip, icon, title, placement: tipPlacement, href
        } = tooltip;

        // avoid destructuring string tooltip setting link to string.prototype.link()
        const link = _.isObject(tooltip) ? tooltip.link : undefined;

        const iconPlacement = (isOnPhone) ? 'inside' : 'outside';

        const tooltipProps = {
            text,
            title,
            icon,
            tipPlacement,
            link,
            href,
            iconPlacement
        };

        if (isOnPhone && !tooltipProps.tipPlacement) {
            tooltipProps.tipPlacement = 'top';
        }

        return <TooltipIcon {...tooltipProps} />;
    }

    /**
     * Render label for this component. Apply 'required' styling as appropriate.
     * @param {ReactElement} [tooltipIcon] - tooltip icon component
     * @returns {ReactElement} JSX for the label
     */
    renderLabel (tooltipIcon) {
        const {
            id, label, required, readOnly, hideLabel, labelClassName, showOptional, theme, secondaryLabel, secondaryLabelClassName
        } = this.props;
        const layoutComponent = this.getLayoutComponent();
        let className = (layoutComponent) ? layoutComponent.labelClass : undefined;
        className = cx(className, labelClassName);
        const labelProps = {
            id,
            label,
            required,
            readOnly,
            hideLabel,
            showOptional,
            theme,
            className,
            secondaryLabel,
            secondaryLabelClassName
        };

        return <FieldLabel {...labelProps} tooltipIcon={tooltipIcon}/>;
    }

    /**
     * Render readonly control for this component.
     *
     * @param {string} className - additional classes to be used
     *
     * @returns {ReactElement} JSX for the control
     */
    renderControlReadOnly (className) {
        const {
            id, value, availableValues, theme
        } = this.props;
        let primaryValue = value;
        let secondaryValue = null;
        if (availableValues && (value !== undefined && value !== null)) {
            const {translator} = this;
            const selectedValue = availableValues.find(option => option.code.toString() === value.toString());
            if (selectedValue) {
                primaryValue = translator(selectedValue.name);
                secondaryValue = translator(selectedValue.subtitle);
            }
        }

        if (this.shouldDisplayDefaultROValue(primaryValue)) {
            primaryValue = FieldComponent.defaultROEmptyValue;
            secondaryValue = null;
        }

        const themeStyles = theme.getStyles('FieldComponent', styles);
        const classes = cx(themeStyles.controlReadOnly, className);
        return (
            <div id={id} data-read-only={true} className={classes}>
                {primaryValue}
                {secondaryValue ? <span className={themeStyles.controlReadOnlySecondary}>{secondaryValue}</span> : ''}
            </div>
        );
    }

    /**
     * Should display default readOnly char?
     *
     * @param {string} value
     *
     * @returns {boolean} true if value is empty
     */
    shouldDisplayDefaultROValue (value) {
        return !value;
    }

    /**
     * Render control for this component.
     *
     * @param {string} inputType - HTML5 input type
     *
     * @returns {ReactElement} JSX for the control
     */
    renderControl (inputType) {
        const {id, className} = this.props;
        // throw error in __DEV__ only; in __TEST__ and __PROD__, the readonly rendering will be used
        if (__DEV__) {
            throw new Error('"renderControl()" method is not overridden in component' +
                `'<${getDisplayName(this)} id="${id}" inputType="${inputType}" />'`);
        }
        return this.renderControlReadOnly(className);
    }

    /**
     * Generates the automation property used for automation tests
     * the property should be visible only in dev or test environmnents
     * @returns {object|undefined} The automation property attribute when gwTest is provided via props
     */
    generateAutomationProperty () {
        if (__DEV__ || __TEST__) {
            const {gwTest} = this.props;
            if (gwTest) {
                return {[ `data-gw-test-${gwTest}` ]: ''};
            }
        }
        return undefined;
    }

    /**
     * generates the data-path attribute
     * @returns {object} the data-path attribute when dataPath is provided via props
     */
    generateDataPathProperty () {
        const {dataPath} = this.props;
        return {'data-path': dataPath};
    }

    /**
     * Render messages for this component. Messages are expected to be translated before passing in.
     *
     * @param {Array} validationMessages - error messages to render
     * @param {string} successMessage - info message to render
     * @param {boolean} isValid - indicates whether the component is valid or not
     * @returns {ReactElement} JSX for the messages
     */
    renderMessages (validationMessages, successMessage, isValid) {
        if (!isValid) {
            validationMessages = validationMessages.filter((msg) => {
                if (msg) {
                    return true;
                }
                log.error('This component is set to show errors, but the validation error message is undefined.');
                return false;
            });
            return <FieldMessage errorMessage={() => validationMessages.map((msg) => <div key={msg}>{msg}</div>)}/>;
        }
        else if (successMessage) {
            return <FieldMessage successMessage={successMessage}/>;
        }
        return null;
    }

    renderValidationIcon (showValidationIcon, readOnly, isValid) {
        if (!showValidationIcon || readOnly) {
            return null;
        }

        const icon = (
            <div className={cx('fa-stack')}>
                <i className={cx(getIconClasses('circle'), 'fa-stack-2x', isValid ? styles.validStateIcon : styles.invalidStateIcon)}/>
                <i className={cx(isValid ? getIconClasses('check') : getIconClasses('exclamation'), 'fa-stack-1x')}/>
            </div>
        );

        const isDirty = !this.pristine && !this.focused;
        if (!isValid || isDirty) {
            return (
                <span className={styles.validationIcon}>
                    {icon}
                </span>
            );
        }

        return null;
    }

    isValid (validationMessages) {
        return !this.showErrors() || _.isEmpty(validationMessages);
    }

    isPhoneDevice (device) {
        return device === 'phone';
    }

    /**
     * Render generic field component. Call helper methods to be the label, control and messages and then
     * apply layout and render
     *
     * @param {String} device JSX device
     * @returns {ReactElement} JSX for this component
     */
    renderFieldComponent (device) {
        const {
            layout, inputType = 'text', className, readOnly,
            tooltip, hideLabel, visible, required, showValidationIcon, successMessage
        } = this.props;
        const layoutComponent = this.getLayoutComponent();

        // Special case if 'visible' property defined to match 'required' property
        let isHidden = (visible === false);
        if (visible === VISIBLE_IF_REQUIRED) {
            isHidden = (required !== true);
        }

        const validationMessages = this.getValidationMessages();
        // If there's validation errors or it's not pristine we need display the field
        if ((validationMessages && !_.isEmpty(validationMessages)) || !this.pristine) {
            isHidden = false;
        }
        const isValid = this.isValid(validationMessages);

        // Skip render if hidden (and not prevented via renderComponent*)
        if (isHidden === true) {
            return null;
        }

        const isOnPhone = this.isPhoneDevice(device);
        const isTooltipNextToControl = !isOnPhone && !hideLabel;
        const tooltipIcon = this.renderTooltipIcon(isOnPhone);

        const automationProperty = this.generateAutomationProperty();
        const labelContent = this.renderLabel(isOnPhone && tooltipIcon);
        const controlContent = (readOnly) ? this.renderControlReadOnly(className) : this.renderControl(inputType, {isValid});
        const messageContent = this.renderMessages(validationMessages, successMessage, isValid);
        const relativeClass = (tooltip && layout !== 'plain' && hideLabel !== true) ? styles.relative : null;

        if (layoutComponent) {
            // calling directly to speed up rendering; React 16 may fix this when using JSX
            return layoutComponent({
                labelContent,
                controlContent,
                messageContent,
                className: cx(relativeClass, className),
                automationProperty
            });
        }

        // Apply simple layout
        const outerStyle = cx(
            {[ styles.fieldComponent ]: layout !== 'plain'},
            {[ styles.reversed ]: layout === 'reversed'},
            {[ styles.fullWidth ]: layout === 'full-width'},
            {[ styles.fullWidthSimple ]: layout === 'full-width-simple'},
            {[ gridStyles.gridField ]: layout === 'grid-field'},
            {[ gridStyles.gridKeepWithPrevious ]: layout === 'grid-keep-with-previous'},
            relativeClass,
            className
        );
        const innerStyle = cx({[ styles.controls ]: layout !== 'plain'});

        const validationIcon = this.renderValidationIcon(showValidationIcon, readOnly, isValid);

        return (
            <div className={outerStyle} {...automationProperty}>
                {labelContent}
                <div className={innerStyle}>
                    <div className={styles.contentContainer}>
                        {controlContent}
                        {validationIcon}
                        {isTooltipNextToControl && tooltipIcon}
                    </div>
                    {messageContent}
                </div>
            </div>
        );
    }

    render () {
        return renderWithBreakpoint(device => this.renderFieldComponent(device));
    }

    getLayoutComponent = () => ((this.props.layout === 'inline') ? InlineFieldLayout : undefined);
}
